import {
  bookingId,
  clashesFor,
  type EquipmentBooking,
} from '../../domain/entities/equipment-booking.ts';
import { isBookable, type Equipment } from '../../domain/entities/equipment.ts';

/**
 * Choosing which machine to book when a template asks for a kind of one
 * (SCRUM-210).
 *
 * A template can only honestly carry a **type** — "an excavator is needed
 * here" — because it is applied to jobs that do not exist yet and a
 * company's `EX-04` is not another's. Applying it has to turn that into a
 * unit, and the maintainer's decision is the one this file implements:
 *
 * > auto-pick a unit that is available; otherwise it just selects one and
 * > gives the conflict notification that there might be a scheduling
 * > issue with this job's equipment
 *
 * Which is right, and consistent with the picker: **warn, never refuse**.
 * The alternative outcomes are both worse. Refusing to apply the template
 * because the yard is busy makes a planning tool unusable in the season it
 * matters. Leaving the item unbooked is the one failure nobody can see —
 * the schedule would look complete and the plant would simply not be
 * there.
 *
 * Pure, and given the fleet and the bookings rather than fetching them, so
 * the choice is testable without a database and so the caller can show
 * what a template is about to do before it does it.
 */
export interface PlantPick {
  readonly equipmentTypeId: string;
  /** The unit chosen, or absent when the company owns none of that type. */
  readonly equipmentId?: string;
  readonly name?: string;
  /**
   * Chosen despite being wanted elsewhere, because nothing of that type
   * was free. The caller shows this; it never blocks the apply.
   */
  readonly contested: boolean;
}

export interface PickPlantInput {
  readonly fleet: readonly Equipment[];
  /** Bookings that already exist, across every job. */
  readonly existing: readonly EquipmentBooking[];
  readonly companyId: string;
  readonly jobId: string;
  readonly jobName: string;
}

/**
 * What one activity needs, and when.
 *
 * `scheduleItemId` is the id the item *will* have — generated before the
 * write, the way every id in this product is, so a booking can be decided
 * and shown before anything is saved.
 */
export interface PlantRequest {
  readonly scheduleItemId: string;
  readonly title: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly equipmentTypeIds: readonly string[];
}

export interface PickedPlant {
  /** Per request, in the order they were asked for. */
  readonly picks: ReadonlyMap<string, readonly PlantPick[]>;
  /** Bookings the apply would create, ready to show or to write. */
  readonly bookings: readonly EquipmentBooking[];
  /** Types the company owns no machine of. Named, never silently skipped. */
  readonly missingTypes: readonly string[];
  readonly contestedCount: number;
}

export function pickPlant(input: PickPlantInput, requests: readonly PlantRequest[]): PickedPlant {
  const picks = new Map<string, PlantPick[]>();
  const bookings: EquipmentBooking[] = [];
  const missing = new Set<string>();
  let contested = 0;

  /**
   * Grows as the template is walked.
   *
   * The whole point: two activities in the same template that overlap must
   * not both be handed the same machine. Checking only against what was
   * already in the database would do exactly that, and the conflict would
   * appear the moment the template was applied — caused by the apply
   * itself.
   */
  const known: EquipmentBooking[] = [...input.existing];

  for (const request of requests) {
    const made: PlantPick[] = [];

    for (const equipmentTypeId of request.equipmentTypeIds) {
      const candidates = input.fleet.filter(
        (machine) => machine.typeId === equipmentTypeId && isBookable(machine),
      );

      if (candidates.length === 0) {
        // The company owns none of this type, or every one is retired or
        // out for maintenance. Named rather than skipped: "we have no
        // excavator" is something the person applying the template needs
        // to read, and an item that silently came back unbooked would not
        // say it.
        missing.add(equipmentTypeId);
        made.push({ equipmentTypeId, contested: false });
        continue;
      }

      const proposalFor = (machine: Equipment): EquipmentBooking => ({
        id: bookingId(request.scheduleItemId, machine.id),
        companyId: input.companyId,
        equipmentId: machine.id,
        jobId: input.jobId,
        scheduleItemId: request.scheduleItemId,
        jobName: input.jobName,
        title: request.title,
        startDate: request.startDate,
        endDate: request.endDate,
        // A template never publishes a schedule by itself, so anything it
        // books is on an unpublished one until somebody says otherwise.
        scheduleOnline: false,
        completed: false,
      });

      const free = candidates.find(
        (machine) => clashesFor(proposalFor(machine), known).length === 0,
      );
      // Nothing free: take the first anyway and say so. Refusing would
      // make the tool unusable in the season it matters; leaving it
      // unbooked is the failure nobody can see.
      const chosen = free ?? candidates[0]!;
      const booking = proposalFor(chosen);

      known.push(booking);
      bookings.push(booking);
      if (!free) contested += 1;
      made.push({
        equipmentTypeId,
        equipmentId: chosen.id,
        name: chosen.name,
        contested: !free,
      });
    }

    picks.set(request.scheduleItemId, made);
  }

  return {
    picks,
    bookings,
    missingTypes: [...missing],
    contestedCount: contested,
  };
}
